import "./dashboard.scss";
import { useContext, useEffect, useState } from "react";
import { AnimatePresence, useSpring, useTransform } from "motion/react";
import * as motion from "motion/react-client";
import { dashboardTransition } from "../transitions";
import { challenges } from "../contexts/ChallengeData";
import { ProfileContext } from "../contexts/AppContext";
import { formatTime, happinessAngle } from "../services/scoringService";

export default function Dashboard() {
  const { currentChallenge, battery, happiness, time } = useContext(ProfileContext);
  const [lastHappiness, setLastHappiness] = useState(happiness);
  const [deltaHappiness, setDeltaHappiness] = useState(0);
  if (currentChallenge === undefined) return null;

  const progressSteps = challenges.length;

  const [formattedTime, setFormattedTime] = useState("On Time");
  const animatedTime = useSpring(time, { bounce: 0, duration: 2000 });
  animatedTime.on("change", (value) => {
    const formatted = formatTime(value);
    let timeString;
    if (formatted === "0:00") {
      timeString = "On Time";
    } else if (formatted.startsWith("-")) {
      timeString = `${formatted.slice(1)} ahead`;
    } else {
      timeString = `${formatted} late`;
    }

    setFormattedTime(timeString);
  });

  const animatedBattery = useSpring(battery, { bounce: 0 });
  const batteryFillHeight = useTransform(animatedBattery, [0, 100], [1, 47]);

  useEffect(() => {
    animatedBattery.set(battery);
  }, [battery]);

  useEffect(() => {
    animatedTime.set(time);
  }, [time]);

  // every time happiness changes, show a toast and then hide it after a short delay
  const [showHappinessToast, setShowHappinessToast] = useState(false);
  useEffect(() => {
    console.log("Happiness changed:", happiness, "Last Happiness:", lastHappiness);
    setLastHappiness(happiness);
    setDeltaHappiness(happiness - lastHappiness);
    
    if (happiness - lastHappiness === 0) return;
    setShowHappinessToast(true);
    const timer = setTimeout(() => setShowHappinessToast(false), 2000);
    return () => clearTimeout(timer);
  }, [happiness]);

  return (
    <AnimatePresence>
      <motion.section className="dashboard" {...dashboardTransition}>
        <section className="dashboard__section">
          <div className="dashboard__gauge">
            <div className="time">
              <motion.pre className="time__display">{formattedTime}</motion.pre>
            </div>
            <div className="dashboard__label">Time</div>
          </div>
          <div className="dashboard__gauge">
            <div className="happiness">
              <img className="happiness__background" src="gauge-happiness.svg" />
              <motion.div
                className="happiness__arrow-container"
                animate={{
                  transform: `rotate(${happinessAngle(happiness)}deg)`,
                  transition: { type: "spring", mass: 1 },
                }}
              >
                <img className="happiness__arrow" src="gauge-happiness-arrow.svg" />
              </motion.div>
              {showHappinessToast && (
                <motion.div
                  // add classname if happiness is positive or negative
                  className={`happiness__toast ${
                    deltaHappiness > 0 ? "happiness__toast--positive" : "happiness__toast--negative"
                  }`}
                  initial={{ opacity: 0, y: 50, scale: 0.3 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.5 }}
                >
                  {deltaHappiness > 0 ? `+${deltaHappiness}` : deltaHappiness}
                </motion.div>
              )}
            </div>
            <div className="dashboard__label">Happiness</div>
          </div>
          <div className="dashboard__gauge">
            <div className="battery">
              <div className="battery__background">
                <img src="battery-bg.svg" />
              </div>
              <div className="battery__level">
                <img src="battery-top.svg" />
                <motion.img className="battery__fill" src="battery-fill.png" style={{ height: batteryFillHeight }} />
                <img src="battery-bottom.svg" />
              </div>
            </div>
            <div className="dashboard__label">Battery</div>
          </div>
        </section>
        <section className="dashboard__section">
          <div className="dashboard__progress">
            <ul className="progress">
              {Array.from({ length: progressSteps }).map((_, index) => (
                <li
                  key={index}
                  className={`progress__step ${index <= currentChallenge ? "progress__step--active" : ""}`}
                />
              ))}
            </ul>
          </div>
        </section>
      </motion.section>
    </AnimatePresence>
  );
}
